import { ArrowBackRounded, ArrowForwardRounded } from "@material-ui/icons";
import React, { useState } from "react";

import { LinkedRecord } from "../domain/LinkedRecord";
import { useNavigate } from "../hooks/useNavigate";
import {
  Divider,
  Link,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import { RemoveLinkedRecordDialog } from "./RemoveLinkedRecordDialog";
import {
  LinkedRecordViewModel,
  toViewModel,
} from "../domain/LinkedRecordViewModel";
import { getLinkRecordTypeTitle } from "../domain/LinkedRecordType";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { Model } from "@ofqual/portal-core/core/types";
import { useAuth } from "../hooks/useAuth";
import { UserType } from "../redux/types";

export type LinkedRecordItemProps = {
  viewModel: LinkedRecordViewModel;
  entityReferenceId: string;
};

const PADDING = 15;
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 20,
    marginTop: 10,
  },
  activity: {
    flexGrow: 1,
    padding: PADDING,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
  avatar: {
    marginLeft: "auto",
    paddingLeft: 15,
    paddingRight: 5,
    paddingTop: 0,
    paddingBottom: 0,
    textAlign: "center",
    lineHeight: "2.5em",
  },
  body: {
    minHeight: 70,
    paddingTop: PADDING,
    paddingBottom: PADDING,
    display: "inline-block",
    left: "20px",
    position: "relative",
    maxWidth: "470px",
  },
  options: {
    textAlign: "right",
    paddingTop: PADDING,
  },
  option: {
    fontWeight: "bold",
    marginLeft: "5px",
  },
  iconSection: {
    display: "inline-block",
    backgroundColor: "#aaa",
    borderRadius: "50%",
    width: "60px",
    height: "60px",
    verticalAlign: "top",
    top: "15px",
    paddingTop: "16px",
    paddingLeft: "16px",
  },
}));

export const LinkedRecordItem: React.FC<LinkedRecordItemProps> = ({
  viewModel,
  entityReferenceId,
}) => {
  const classes = useStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { userType } = useAuth();
  const allowEdit = userType !== UserType.Magic;

  const navigate = useNavigate(viewModel);

  const displayLinkMeta = (meta: string) => {
    const model: Model = JSON.parse(meta);
    return (
      <>
        {Object.keys(model).map(k => (
          <Typography variant="body1"><strong>{k}: </strong>{model[k]}</Typography>
        ))}
      </>
    )
  }

  return (
    <>
      <div className={classes.root}>
        <Paper square className={classes.activity} data-test="action">
          <div className={classes.iconSection}>
            {viewModel.linkedEntityRelationship === "SOURCE" ? (
              <ArrowBackRounded />
            ) : (
              <ArrowForwardRounded />
            )}
          </div>
          <div className={classes.body} data-test="action-body">
            <Typography variant="body1">
              <strong>{viewModel.linkedEntityTitle}</strong>
            </Typography>
            {!!viewModel.linkMeta && displayLinkMeta(viewModel.linkMeta)}
            <br />
            <strong>Created: </strong>
            {formatUtcDate(
              viewModel.createdDate,
              standardDateFormats.DATE
            )}{" "}
            <strong>Created by: </strong>
            {viewModel.createdByDisplayName}
            {/* <br />
            <strong>Type: </strong> {type} */}
          </div>
          <Divider />
            {allowEdit && (
              <div className={classes.options}>
                <Link
                  data-test="view-record-button"
                  component="button"
                  className={classes.option}
                  onClick={() => navigate()}
                >
                  View record
                </Link>
                <Link
                  data-test="remove-link-button"
                  component="button"
                  className={classes.option}
                  onClick={() => setIsDialogOpen(true)}
                >
                  Remove
                </Link>
              </div>
            )}
        </Paper>
      </div>
      {allowEdit && (
        <RemoveLinkedRecordDialog
          entityReferenceId={entityReferenceId}
          linkedRecord={viewModel}
          isDialogOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </>
  );
};
